import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogContent, MatDialogRef} from '@angular/material/dialog';
import {MatIcon} from '@angular/material/icon';
import {Store} from '@ngrx/store';
import {catchError, from, Observable, of} from 'rxjs';
import {Environment, NotificationService} from '@raven';
import {SelectPipeModule} from '../../../shared/pipes/select-pipe/select-pipe.module';
import {SquareAppDetails} from '@store/account/account.types';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {RvFormsModule} from '../../../shared/form-fields/rv-forms.module';
import {AccountActions} from '@store/account/account.actions';
import {MatButton} from '@angular/material/button';
import {CommonValidators} from '@store/common/common.validators';

interface SquareApi {
  payments: (appId: string, locationId: string) => SquarePayments;
}

interface SquarePayments {
  card: () => Observable<SquareCard>;
}

interface SquareCard {
  attach(containerId: string): void;

  token?: string;
  details?: {
    card: {
      last4: string;
    }
  };
}

export interface MakeAPaymentDialogData {
  squareAppDetails: SquareAppDetails;
  currentAccountBalance: number;
}

@Component({
  selector: 'rn-make-a-payment',
  templateUrl: './make-a-payment.dialog.html',
  styleUrl: './make-a-payment.dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogContent, MatIcon, SelectPipeModule, RvFormsModule, MatButton]
})
export class MakeAPaymentDialog implements OnInit, OnDestroy {
  public readonly dialogData: MakeAPaymentDialogData = inject(MAT_DIALOG_DATA);
  public readonly squareAppDetails: SquareAppDetails = this.dialogData.squareAppDetails;
  public readonly currentAccountBalance: number = this.dialogData.currentAccountBalance;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly dialogRef: MatDialogRef<MakeAPaymentDialog> = inject(MatDialogRef);
  private readonly environment = inject(Environment);
  private readonly fb = inject(FormBuilder);
  private readonly notificationService = inject(NotificationService);
  private readonly store = inject(Store);
  private readonly loadSquareScriptListener = () => this.initializeSquare();
  private squareScript: HTMLElement;
  private card;
  paymentForm: FormGroup;

  ngOnInit() {
    this.paymentForm = this.fb.group({
      amount: ['', {validators: [Validators.required, CommonValidators.minPayment(5), CommonValidators.currencyMax(this.currentAccountBalance)]}],
    });
    this.loadSquareScript();
  }

  ngOnDestroy() {
    this.squareScript?.removeEventListener('load', this.loadSquareScriptListener);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private loadSquareScript(): void {
    const id = 'squareScript';
    const body: HTMLElement = document.body;
    this.squareScript = document.getElementById(id);
    if (this.squareScript === null) {
      this.squareScript = document.createElement('script');
      this.squareScript.setAttribute('id', id);
      this.squareScript.setAttribute('src', this.environment.squareWebSdkUrl);
      body.appendChild(this.squareScript);
      this.squareScript.addEventListener('load', this.loadSquareScriptListener);
    } else {
      this.initializeSquare();
    }
  }

  private initializeSquare(): void {
    const squareApi: SquareApi = window['Square'];
    if (!squareApi) {
      throw new Error('Square.js failed to load properly');
    }
    const payments = squareApi.payments(this.squareAppDetails.appId, this.squareAppDetails.locationId);
    from(payments.card())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((card) => {
        this.card = card;
        card.attach('#card-container');
      });
  }

  public onClickPay(): void {
    if (!this.card) {
      return;
    }
    from(this.card.tokenize()).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError((error: unknown) => {
        this.notificationService.showSnackbarError(`Invalid payment details.`);
        return of(error);
      })
    ).subscribe((result: SquareCard) => {
        const token = result.token;
        if (token) {
          this.store.dispatch(AccountActions.submitMakeAPayment({
            ...this.paymentForm.value,
            token,
            last4: result.details.card.last4
          }));
          this.dialogRef.close(false);
        }
      }
    );
  }
}
